// Client-side image resize. Canvas + toBlob, no dependencies.
// Returns a Blob (or the original File when no resize is needed).
// Throws ResizeError on inputs canvas can't draw or files >25 MB.

export class ResizeError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'ResizeError'
    this.code = code
  }
}

const MAX_INPUT_BYTES = 25 * 1024 * 1024
const PASSTHROUGH_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export async function resizeImage(file, { maxEdge, quality = 0.85 } = {}) {
  if (!file || typeof file !== 'object') throw new ResizeError('NO_FILE', 'No file provided')
  if (!file.type?.startsWith('image/')) throw new ResizeError('NOT_IMAGE', 'Not an image')
  if (file.size > MAX_INPUT_BYTES) throw new ResizeError('TOO_LARGE', 'File exceeds 25 MB')
  if (!Number.isFinite(maxEdge) || maxEdge <= 0) throw new ResizeError('BAD_OPTS', 'maxEdge required')

  // gif/webp are never canvas-flattened. Canvas keeps only the first frame,
  // which would silently destroy animation (and silently turn an animated
  // cover into a still the server then accepts). Pass these bytes to the
  // server untouched and let it be the format/animation gate: validateImageBytes
  // accepts animated gif/webp on avatar+banner, and rejects them (ANIMATED) or
  // rejects gif (FORMAT) on the static surfaces — surfacing an honest error
  // instead of a silent flatten. Static jpeg/png still resize below.
  if (file.type === 'image/gif' || file.type === 'image/webp') {
    return file
  }

  const objectUrl = URL.createObjectURL(file)
  let img
  try {
    img = await loadImage(objectUrl)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }

  const w = img.naturalWidth
  const h = img.naturalHeight
  if (!w || !h) throw new ResizeError('BAD_DIMENSIONS', 'Image has no dimensions')

  const longestEdge = Math.max(w, h)
  if (longestEdge <= maxEdge && PASSTHROUGH_MIMES.has(file.type)) {
    return file
  }

  const scale = longestEdge > maxEdge ? maxEdge / longestEdge : 1
  const targetW = Math.max(1, Math.round(w * scale))
  const targetH = Math.max(1, Math.round(h * scale))

  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new ResizeError('NO_CTX', 'Canvas 2D context unavailable')
  ctx.drawImage(img, 0, 0, targetW, targetH)

  const outMime = PASSTHROUGH_MIMES.has(file.type) ? file.type : 'image/jpeg'

  const blob = await new Promise(resolve => canvas.toBlob(resolve, outMime, quality))
  if (!blob) throw new ResizeError('TOBLOB_FAILED', 'Canvas toBlob returned null')
  return blob
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new ResizeError('UNDRAWABLE', 'Image failed to decode'))
    img.src = src
  })
}
